import * as React from 'react'
import { cn } from '../../lib/utils'

export interface PropertyDiffRow {
  name: string
  /** Rendered cell content; undefined = the property is absent on that side. */
  before?: React.ReactNode
  after?: React.ReactNode
  /** Full-text hover titles when the rendered content is clipped. */
  beforeTitle?: string
  afterTitle?: string
}

/**
 * The property-level table inside an expanded diff row: `Property | Old | New`
 * (old red, new green), or a single value column when the entity has no
 * before side (adds, removes, lean commit deltas). Content arrives rendered;
 * an absent side reads `∅`.
 */
export function PropertyDiffTable({
  rows,
  columns = 'before-after',
  beforeHeader = 'Before',
  afterHeader = 'After',
  valueHeader = 'Value',
  struck = false
}: {
  rows: PropertyDiffRow[]
  /** Two value columns (a real diff) or one (a plain property listing). */
  columns?: 'before-after' | 'value'
  beforeHeader?: string
  afterHeader?: string
  valueHeader?: string
  /** Cross the values off — the entity is being removed. */
  struck?: boolean
}) {
  if (rows.length === 0) return null
  const struckCls = struck && 'text-ink-3 line-through'
  return (
    <table className="w-full table-fixed text-left text-11">
      <thead className="text-ink-3">
        <tr>
          <th
            className={cn(
              'px-2 py-1 text-10 font-medium tracking-wider uppercase',
              columns === 'before-after' ? 'w-[40%]' : 'w-[35%]'
            )}
          >
            Property
          </th>
          {columns === 'before-after' ? (
            <>
              <th className="w-[30%] px-2 py-1 text-10 font-medium tracking-wider uppercase">
                {beforeHeader}
              </th>
              <th className="w-[30%] px-2 py-1 text-10 font-medium tracking-wider uppercase">
                {afterHeader}
              </th>
            </>
          ) : (
            <th className="px-2 py-1 text-10 font-medium tracking-wider uppercase">
              {valueHeader}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name} className="border-t border-line/60">
            <td className={cn('px-2 py-1 font-mono break-all text-ink-2', struckCls)}>
              {row.name}
            </td>
            {columns === 'before-after' ? (
              <>
                <td
                  className={cn('px-2 py-1 font-mono break-all text-st-down', struckCls)}
                  title={row.beforeTitle}
                >
                  {row.before ?? '∅'}
                </td>
                <td
                  className={cn('px-2 py-1 font-mono break-all text-st-run', struckCls)}
                  title={row.afterTitle}
                >
                  {row.after ?? '∅'}
                </td>
              </>
            ) : (
              <td
                className={cn('px-2 py-1 font-mono break-all text-ink', struckCls)}
                title={row.afterTitle}
              >
                {row.after ?? '∅'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
